import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { appConfigApi, type AppConfigDocument } from '@/api/appConfig';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useConfirm } from '@/components/ui/ConfirmProvider';
import { Input } from '@/components/ui/Input';
import { minVersionRaises, serverKnowsLatestVersion, versionFieldError } from '@/lib/appVersionsAdmin';
import { humanizeError } from '@/lib/errors';

/**
 * «Версии приложения iOS» (15.10.2026): `minVersion.ios` и `latestVersion.ios` полями вместо `PUT` руками.
 * Android здесь не задаётся (правило 42): в Google Play нет новой сборки, а 100 и 101 различить нельзя.
 *
 * Тот же запрос, что у страницы флагов (`['admin','app-config']`); ответ на правку — документ целиком, его и кладём.
 * Подъём минимальной версии — только через подтверждение: он запирает учеников на старых версиях.
 *
 * Раскладка на телефоне — по договорённости с терминалом 4 (CURATOR-MOBILE-DESIGN §5.18): поля в один столбец
 * (`grid-cols-1`, от `sm` — два), без фиксированных ширин, кнопка во всю ширину, у поля — подпись, а не заглушка.
 */
export function AppVersionsCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const ask = useConfirm();
  const config = useQuery({ queryKey: ['admin', 'app-config'], queryFn: appConfigApi.get });
  const savedMin = config.data?.minVersion?.ios ?? '';
  const savedLatest = config.data?.latestVersion?.ios ?? '';
  const latestKnown = serverKnowsLatestVersion(config.data);

  // null — поле не трогали, показываем сохранённое.
  const [minDraft, setMinDraft] = useState<string | null>(null);
  const [latestDraft, setLatestDraft] = useState<string | null>(null);
  const min = minDraft ?? savedMin;
  const latest = latestDraft ?? savedLatest;

  const save = useMutation({
    mutationFn: ({ key, value }: { key: 'minVersion.ios' | 'latestVersion.ios'; value: string }) =>
      appConfigApi.set(key, value),
    onSuccess: (doc: AppConfigDocument, { key }) => {
      qc.setQueryData(['admin', 'app-config'], doc);
      if (key === 'minVersion.ios') setMinDraft(null);
      else setLatestDraft(null);
    },
  });

  const minError = versionFieldError('min', min, latestKnown ? latest : '');
  const latestError = versionFieldError('latest', latest, min);
  const errorText = (e: ReturnType<typeof versionFieldError>) =>
    e === 'format' ? t('adminAppConfig.versions.errFormat') : e === 'min_above_latest' ? t('adminAppConfig.versions.errMinAboveLatest') : undefined;

  const saveMin = async () => {
    const value = min.trim();
    if (minVersionRaises(savedMin, value)) {
      const ok = await ask({
        title: t('adminAppConfig.versions.minConfirmTitle'),
        message: t('adminAppConfig.versions.minConfirm', { version: value }),
        confirmLabel: t('adminAppConfig.versions.minConfirmCta'),
        danger: true,
      });
      if (!ok) return;
    }
    save.mutate({ key: 'minVersion.ios', value });
  };

  const current = (v: string) => t('adminAppConfig.versions.current', { value: v || t('adminAppConfig.versions.off') });

  return (
    <Card>
      <h2 className="mb-1 font-display text-base font-bold text-on-surface">{t('adminAppConfig.versions.title')}</h2>
      <p className="mb-4 text-sm text-on-surface-variant">{t('adminAppConfig.versions.note')}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-2">
          <Input
            label={t('adminAppConfig.versions.minLabel')}
            value={min}
            inputMode="decimal"
            autoCapitalize="off"
            autoCorrect="off"
            disabled={config.isLoading}
            error={errorText(minError)}
            onChange={(e) => setMinDraft(e.target.value)}
          />
          <p className="text-xs text-on-surface-variant">{t('adminAppConfig.versions.minHint')}</p>
          <p className="break-all text-xs text-on-surface-variant">{current(savedMin)}</p>
          <Button
            className="w-full sm:w-auto"
            disabled={config.isLoading || min.trim() === savedMin || !!minError}
            loading={save.isPending && save.variables?.key === 'minVersion.ios'}
            onClick={() => void saveMin()}
          >
            {t('adminAppConfig.versions.save')}
          </Button>
        </div>
        <div className="min-w-0 space-y-2">
          <Input
            label={t('adminAppConfig.versions.latestLabel')}
            value={latest}
            inputMode="decimal"
            autoCapitalize="off"
            autoCorrect="off"
            disabled={config.isLoading || !latestKnown}
            error={latestKnown ? errorText(latestError) : undefined}
            onChange={(e) => setLatestDraft(e.target.value)}
          />
          <p className="text-xs text-on-surface-variant">
            {latestKnown ? t('adminAppConfig.versions.latestHint') : t('adminAppConfig.versions.serverOld')}
          </p>
          {latestKnown && <p className="break-all text-xs text-on-surface-variant">{current(savedLatest)}</p>}
          <Button
            className="w-full sm:w-auto"
            disabled={config.isLoading || !latestKnown || latest.trim() === savedLatest || !!latestError}
            loading={save.isPending && save.variables?.key === 'latestVersion.ios'}
            onClick={() => save.mutate({ key: 'latestVersion.ios', value: latest.trim() })}
          >
            {t('adminAppConfig.versions.save')}
          </Button>
        </div>
      </div>
      {(config.isError || save.isError) && (
        <p className="mt-3 text-sm text-error">{humanizeError(config.error ?? save.error)}</p>
      )}
    </Card>
  );
}
